import { ChevronDown, ChevronUp, Download, FileText, Sheet } from 'lucide-react'
import React, { useState } from 'react'
import { CSVLink } from 'react-csv'
import { type Data, type Headers } from 'react-csv/lib/core'

import { Button, ButtonLink, DropdownMenu, Modal } from 'slab'
import { type EnrichedMemberInvoice } from 'utils/employer_invoice_utils'
import { usePostEmployersReportingEmployerBillingInvoice } from 'utils/web_api'

import { getFormattedDate, } from '../utils'

interface DownloadEmployerInvoiceDropdownProps {
  filteredRows: EnrichedMemberInvoice[]
  employerInvoiceId: string
  periodStartDate: string
  periodEndDate: string
  reportDownloadUrl?: string
  showPlanYear: boolean
}

export function DownloadEmployerInvoiceDropdown ({
  filteredRows,
  employerInvoiceId,
  periodStartDate,
  periodEndDate,
  reportDownloadUrl,
  showPlanYear = false
}: DownloadEmployerInvoiceDropdownProps) {
  const [showReportGenerationModal, setShowReportGenerationModal] = useState(false)
  const [isGeneratingReport, setIsGeneratingReport] = useState(false)

  const createReportMutation = usePostEmployersReportingEmployerBillingInvoice()

  const startDateUTC = periodStartDate
    ? getFormattedDate(periodStartDate, { timeZone: 'UTC' })
    : 'NA'
  const endDateUTC = periodEndDate
    ? getFormattedDate(periodEndDate, { timeZone: 'UTC' })
    : 'NA'

  /**
     ****************** CSV Helpers ******************
     */

  const csvHeaders: Headers = [
    { label: 'Employee ID', key: 'employee_id' },
    { label: 'Full Name', key: 'full_name' },
    { label: 'Scheduled Payments', key: 'amounts.total_insurance_premiums.value' },
    { label: 'Coverage Corrections', key: 'amounts.coverage_corrections.value' },
    { label: 'Other', key: 'amounts.other.value' },
    { label: 'Total', key: 'amounts.total.value' },
  ]

  if (showPlanYear) {
    csvHeaders.push({ label: 'Plan Year', key: 'plan_year' })
  }

  const fetchDataForCSV = (): Data => {
    return filteredRows
  }

  const handleGenerateInvoiceReport = async () => {
    setIsGeneratingReport(true)

    try {
      await createReportMutation.mutateAsync({
        data: {
          employer_invoice_id: employerInvoiceId,
        }
      })

      setShowReportGenerationModal(false)
    } catch (error) {
      console.error('Failed to create report:', error)
    } finally {
      setIsGeneratingReport(false)
    }
  }

  const isDisabled = filteredRows.length === 0

  const csvLinkRef = React.useRef<any>(null)
  const buttonLinkRef = React.useRef<any>(null)

  const handleDownloadCSVClick = () => {
    if (csvLinkRef.current) {
      csvLinkRef.current.link.click()
    }
    // Return false to prevent menu from closing automatically
    return false
  }

  const handleDownloadPDFClick = () => {
    if (reportDownloadUrl && buttonLinkRef.current) {
      buttonLinkRef.current.click()
    } else {
      setShowReportGenerationModal(true)
    }
    // Return false to prevent menu from closing automatically
    return false
  }

  return (
    <>
      <DropdownMenu
        trigger={({ open }) => (
          <Button
            variant='tertiary'
            disabled={isDisabled}
            rightIcon={open ? ChevronUp : ChevronDown}
            leftIcon={Download}
          >
            Download
          </Button>
        )}
        align='end'
      >
        <DropdownMenu.Item
          leftIcon={Sheet}
          onSelect={handleDownloadCSVClick}
        >
          CSV Report
        </DropdownMenu.Item>

        <DropdownMenu.Item
          leftIcon={FileText}
          onSelect={handleDownloadPDFClick}
        >
          Invoice PDF
        </DropdownMenu.Item>
      </DropdownMenu>

      <CSVLink
        ref={csvLinkRef}
        data={fetchDataForCSV()}
        headers={csvHeaders}
        filename={`invoice-${periodStartDate}_${periodEndDate}.csv`}
        target='_blank'
        style={{ display: 'none' }}
        data-testid='csv-download-link'
      />

      <ButtonLink
        ref={buttonLinkRef}
        href={reportDownloadUrl}
        download
        disabled={!reportDownloadUrl}
        target='_blank'
        style={{ display: 'none' }}
      >
        PDF Download
      </ButtonLink>

      <Modal
        open={showReportGenerationModal}
        onOpenChange={setShowReportGenerationModal}
        width='medium'
      >
        <Modal.Header>
          <Modal.Title>Request Invoice PDF</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='space-y-4'>
            <div>
              <p className='mb-3'>
                This report includes a breakdown of your invoice covering the period from {startDateUTC} to {endDateUTC}.
              </p>
            </div>

            <p className='text-sm'>
              This report may take a few minutes to generate.
              You'll receive an email with a download link once it's ready.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='flex justify-end gap-3 w-full'>
            <Modal.Button
              variant='secondary'
              disabled={isGeneratingReport}
            >
              Cancel
            </Modal.Button>
            <Button
              variant='primary'
              onClick={handleGenerateInvoiceReport}
              disabled={isGeneratingReport}
              leftIcon={isGeneratingReport ? undefined : FileText}
            >
              {isGeneratingReport ? 'Generating...' : 'Generate report'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}
